/*
 * Author: zoesvision.com
 * Purpose: Mount nested product menus and switch their product panels.
 */

(() => {
const normalizeZMenuTitle = (value = '') => value.trim().toLocaleLowerCase();

class ZHeaderProductMenu extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.bindControls();
    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };
    const isMobile = this.dataset.zProductMenuLayout === 'mobile';

    if (isMobile) {
      this.querySelectorAll('[data-z-mobile-disclosure-level]').forEach((disclosure) => {
        disclosure.addEventListener('toggle', this.onMobileToggle, options);
      });
    } else {
      this.addEventListener('pointerover', this.onPointerOver, options);
      this.addEventListener('focusin', this.onFocusIn, options);
      this.addEventListener('click', this.onClick, options);
      this.addEventListener('keydown', this.onKeyDown, options);

      const firstPanel = this.querySelector('[data-z-product-panel]');
      if (firstPanel) this.activate(firstPanel.dataset.zProductPanel);
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  bindControls() {
    const categoryConfigs = new Map();

    this.querySelectorAll(
      '.z-header-product-menu__panels > [data-z-menu-config-level="2"], .z-header-product-menu__mobile-panels > [data-z-menu-config-level="2"]',
    ).forEach((category) => {
      categoryConfigs.set(normalizeZMenuTitle(category.dataset.zMenuTitle), category);
    });

    this.querySelectorAll('[data-z-menu-level="2"][data-z-menu-title]').forEach((control) => {
      const category = categoryConfigs.get(normalizeZMenuTitle(control.dataset.zMenuTitle));
      if (category) control.dataset.zMenuGroup = category.dataset.zMenuKey;
    });

    this.querySelectorAll('[data-z-menu-level="3"][data-z-menu-title]').forEach((control) => {
      const category = categoryConfigs.get(normalizeZMenuTitle(control.dataset.zParentTitle));
      if (!category) return;

      const subcategory = Array.from(
        category.querySelectorAll(':scope > .z-header-product-menu__nested-configs > [data-z-menu-config-level="3"]'),
      ).find((candidate) => normalizeZMenuTitle(candidate.dataset.zMenuTitle) === normalizeZMenuTitle(control.dataset.zMenuTitle));

      if (subcategory) control.dataset.zMenuGroup = subcategory.dataset.zMenuKey;
    });
  }

  onPointerOver = (event) => this.activateFromEvent(event);

  onFocusIn = (event) => this.activateFromEvent(event);

  onClick = (event) => {
    const control = event.target.closest('[data-z-menu-group]');
    if (!control || !this.contains(control)) return;
    this.activate(control.dataset.zMenuGroup);
  };

  onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const control = event.target.closest('[data-z-menu-group]');
    if (!control || !this.contains(control)) return;
    this.activate(control.dataset.zMenuGroup);
  };

  onMobileToggle = (event) => {
    const disclosure = event.currentTarget;
    if (!this.contains(disclosure)) return;

    if (!disclosure.open) {
      if (disclosure.dataset.zMobileDisclosureLevel === '3') {
        const siblingList = disclosure.parentElement?.parentElement;
        if (siblingList?.querySelector('details[open][data-z-mobile-disclosure-level="3"]')) return;
        const parentDisclosure = disclosure.closest('[data-z-mobile-disclosure-level="2"]');
        const parentControl = parentDisclosure?.querySelector(':scope > summary[data-z-menu-group]');
        if (parentControl) this.activateMobile(parentControl.dataset.zMenuGroup, parentDisclosure);
      }
      return;
    }

    const level = disclosure.dataset.zMobileDisclosureLevel;
    const siblingList = disclosure.parentElement?.parentElement;
    Array.from(siblingList?.children || []).forEach((item) => {
      const sibling = item.querySelector(`:scope > details[open][data-z-mobile-disclosure-level="${level}"]`);
      if (sibling && sibling !== disclosure) sibling.open = false;
    });

    const control = disclosure.querySelector(':scope > summary[data-z-menu-group]');
    if (control) this.activateMobile(control.dataset.zMenuGroup, disclosure);
  };

  activateFromEvent(event) {
    const control = event.target.closest('[data-z-menu-group]');
    if (!control || !this.contains(control)) return;
    this.activate(control.dataset.zMenuGroup);
  }

  activate(groupKey) {
    const panel = this.querySelector(`[data-z-product-panel="${CSS.escape(groupKey)}"]`);
    if (!panel) return;

    this.querySelectorAll('[data-z-product-panel]').forEach((candidate) => {
      const active = candidate === panel;
      candidate.hidden = !active;
      candidate.classList.toggle('z-header-product-menu__panel--active', active);
      candidate.setAttribute('aria-hidden', String(!active));
    });

    this.querySelectorAll('[data-z-menu-group]').forEach((control) => {
      control.setAttribute('aria-selected', String(control.dataset.zMenuGroup === groupKey));
    });
  }

  activateMobile(groupKey, disclosure) {
    const panel = this.querySelector(`.z-header-product-menu__mobile-panels [data-z-product-panel="${CSS.escape(groupKey)}"]`);
    if (!panel) return;

    this.querySelector('[data-z-mobile-active-panel]')?.remove();

    const level = disclosure.dataset.zMobileDisclosureLevel;
    const slot = level === '3'
      ? disclosure.querySelector(':scope > [data-z-mobile-subcategory-panel]')
      : disclosure.querySelector(':scope > .z-header-product-menu__mobile-content > [data-z-mobile-category-panel]');
    if (!slot) return;

    const activePanel = panel.cloneNode(true);
    activePanel.hidden = false;
    activePanel.dataset.zMobileActivePanel = '';
    activePanel.setAttribute('aria-hidden', 'false');
    slot.prepend(activePanel);

    this.querySelectorAll('[data-z-menu-group]').forEach((control) => {
      control.setAttribute('aria-selected', String(control.dataset.zMenuGroup === groupKey));
    });
  }
}

class ZHeaderProductMenuSource extends HTMLElement {
  connectedCallback() {
    if (this.mountFrame) return;
    this.mountFrame = requestAnimationFrame(() => this.mount());
  }

  disconnectedCallback() {
    if (this.mountFrame) cancelAnimationFrame(this.mountFrame);
    this.mountFrame = null;

    (this.mounts || []).forEach(({ target, standardMenu, menu, rootSummary, onRootClick }) => {
      if (target.dataset.zProductMenuSource === this.dataset.zSourceId) {
        delete target.dataset.zProductMenuSource;
        standardMenu.hidden = false;
      }
      rootSummary?.removeEventListener('click', onRootClick, true);
      menu.remove();
    });
    this.mounts = [];
  }

  mount() {
    this.mountFrame = null;
    this.mounts = [];
    this.mountMenu(
      'desktop',
      '.header__inline-menu details[data-z-header-menu-title]',
      ':scope > .mega-menu__content, :scope > .header__submenu',
    );
    this.mountMenu(
      'mobile',
      '#menu-drawer details[data-z-header-mobile-menu-title]',
      ':scope > .menu-drawer__submenu',
    );
    if (this.mounts.length) this.dataset.zMounted = 'true';
  }

  mountMenu(layout, targetSelector, standardSelector) {
    const menu = this.querySelector(`:scope > [data-z-product-menu-layout="${layout}"]`);
    if (!menu?.querySelector('[data-z-product-panel]')) return;

    const trigger = normalizeZMenuTitle(this.dataset.zTopLevelTitle);
    const titleKey = layout === 'mobile' ? 'zHeaderMobileMenuTitle' : 'zHeaderMenuTitle';
    const target = Array.from(document.querySelectorAll(targetSelector)).find(
      (details) => normalizeZMenuTitle(details.dataset[titleKey]) === trigger,
    );
    if (!target || target.dataset.zProductMenuSource) return;

    const standardMenu = target.querySelector(standardSelector);
    if (!standardMenu) return;

    target.dataset.zProductMenuSource = this.dataset.zSourceId;
    standardMenu.hidden = true;
    standardMenu.after(menu);

    let rootSummary = null;
    let onRootClick = null;
    if (layout === 'mobile') {
      rootSummary = target.querySelector(':scope > summary');
      onRootClick = (event) => {
        event.stopImmediatePropagation();
        requestAnimationFrame(() => rootSummary.setAttribute('aria-expanded', String(target.open)));
      };
      rootSummary?.addEventListener('click', onRootClick, true);
    }

    this.mounts.push({ target, standardMenu, menu, rootSummary, onRootClick });
  }
}

if (!customElements.get('z-header-product-menu')) {
  customElements.define('z-header-product-menu', ZHeaderProductMenu);
}

if (!customElements.get('z-header-product-menu-source')) {
  customElements.define('z-header-product-menu-source', ZHeaderProductMenuSource);
}
})();
